import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs';
import { Router } from '@angular/router';
import { IAppState } from '@core/state/state.service';
import { Store } from '@ngrx/store';
import { AppConfigService } from '@core/services/config/app-config.service';

export const GUID_REGEX_STRING =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}';

@Injectable({
  providedIn: 'root',
})
export class ProjectAuthorizationInterceptorService implements HttpInterceptor {
  private readonly apiUrl: string;

  constructor(
    private router: Router,
    private _store: Store,
    private _appConfig: AppConfigService
  ) {
    this.apiUrl = _appConfig.config.apiUrl;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.Forbidden
        ) {
          const projectId = this._projectEntryReadId(req);
          if (projectId !== null) {
            this.router.navigate(['/projects', { projectId }]);
          }
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * The project id when `req` is the project-entry read, `GET {apiUrl}/projects/{projectId}`: the
   * request that decides whether the caller may open the project at all. Only its refusal means
   * "you cannot enter this project" and sends the caller to the project listing (paths 2 and 3
   * below). A 403 from any project sub-resource — an optional statistics or history widget, a
   * write the caller may not make — belongs to the feature that asked, which shows its own refusal.
   * Redirecting on those ejected a project administrator from Stage Overview when one history
   * chart was refused (staging, 2026-10-04).
   */
  private _projectEntryReadId(req: HttpRequest<unknown>): string | null {
    if (req.method !== 'GET') {
      return null;
    }
    const projectEntryRead = new RegExp(
      `^${this.escapeRegExp(this.apiUrl)}/projects/(${GUID_REGEX_STRING})/?(?:\\?.*)?$`
    );
    return projectEntryRead.exec(req.url)?.[1] ?? null;
  }

  // Code paths for handling HTTP redirect interception
  // 1) http request to access project user is authorized for
  //    - no redirect
  //    - return observable of original HTTP response (or rethrow error)
  // 2) project-entry read of a public project user is not authorized for
  //    - redirect to project listing page with join dialog for given project
  //    - throw error for original HTTP request
  // 3) project-entry read of a private project user is not authorized for
  //    - redirect to project listing page with information dialog
  //      explaining they should contact project owner
  //    - throw error for original HTTP request
  // 4) http request to an endpoint not associated with a project
  //    - no redirect
  //    - return observable of original HTTP response (or rethrow error)
  // 5) 403 from a project sub-resource or a write to the project
  //    - no redirect; the feature that asked handles its own refusal
  //    - throw error for original HTTP request

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }
}
